import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSocket, initiateSocket } from '../socket';
import { AuthContext } from '../context/AuthContext';
import axiosInstance from '../axiosInstance';
import './NotificationBell.css';

function NotificationBell() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const dropdownRef = useRef(null);
  const isSuperAdmin = user?.isSuperAdmin;

  // Fetch pending users on mount (only for super admins)
  useEffect(() => {
    if (!isSuperAdmin) return;

    const fetchPendingUsers = async () => {
      try {
        const res = await axiosInstance.get('/audit/pending-users');
        if (res.data?.pendingUsers) {
          setNotifications(res.data.pendingUsers.map(u => ({
            id: u._id,
            type: 'pending_user',
            message: `${u.email} is awaiting approval`,
            email: u.email,
            // Records predating the accountType field are employees.
            accountType: u.accountType || 'employee',
            name: u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email,
            timestamp: u.createdAt
          })));
        }
      } catch (err) {
        console.error('Failed to fetch pending users:', err);
      }
    };

    fetchPendingUsers();
  }, [isSuperAdmin]);

  // Listen for real-time notifications via WebSocket
  useEffect(() => {
    if (!isSuperAdmin) return;

    initiateSocket();          // ensure the shared socket exists (bell mounts before any page inits it)
    const socket = getSocket();
    if (!socket) return;

    const handleNewPendingUser = (data) => {
      setNotifications(prev => {
        // Avoid duplicates
        if (prev.some(n => n.id === data.userId)) return prev;
        return [{
          id: data.userId,
          type: 'pending_user',
          message: `${data.email} is awaiting approval`,
          email: data.email,
          // Only the internal signup path emits this today, hence the default.
          accountType: data.accountType || 'employee',
          name: data.name || data.email,
          timestamp: new Date().toISOString()
        }, ...prev];
      });
    };

    const handleUserApproved = (data) => {
      // Remove from notifications when user is approved
      setNotifications(prev => prev.filter(n => n.id !== data.userId));
    };

    socket.on('notification:pending-user', handleNewPendingUser);
    socket.on('notification:user-approved', handleUserApproved);

    return () => {
      socket.off('notification:pending-user', handleNewPendingUser);
      socket.off('notification:user-approved', handleUserApproved);
    };
  }, [isSuperAdmin]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notification) => {
    setIsOpen(false);
    // Clicking a notification dismisses it from the bell. If the account is still
    // pending, the mount-time fetch of /audit/pending-users will resurface it on
    // next load; once actually approved, notification:user-approved keeps it gone.
    setNotifications(prev => prev.filter(n => n.id !== notification.id));
    if (notification.type === 'pending_user') {
      // Land on the surface that can actually approve this account — customers
      // and employees are approved on different tabs, and User Management opens
      // on Employees by default.
      const surface = notification.accountType === 'customer' ? 'customers' : 'employees';
      navigate(`/audit-logs?tab=users&surface=${surface}`);
    }
  };

  const clearNotification = (e, id) => {
    e.stopPropagation();
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  // Only render for super admins
  if (!isSuperAdmin) return null;

  return (
    <div className="notification-bell" ref={dropdownRef}>
      <button
        className="bell-button"
        onClick={() => setIsOpen(!isOpen)}
        title="Notifications"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/>
        </svg>
        {notifications.length > 0 && (
          <span className="notification-badge">{notifications.length}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <span>Notifications</span>
            {notifications.length > 0 && (
              <button
                className="clear-all"
                onClick={() => setNotifications([])}
              >
                Clear all
              </button>
            )}
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="no-notifications">No new notifications</div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className="notification-item"
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon pending-user">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </div>
                  <div className="notification-content">
                    <div className="notification-message">{notification.message}</div>
                    <div className="notification-time">{formatTime(notification.timestamp)}</div>
                  </div>
                  <button
                    className="dismiss-btn"
                    onClick={(e) => clearNotification(e, notification.id)}
                    title="Dismiss"
                  >
                    &times;
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
